import { useEditReasons, type EditReason, type EditReasonRow } from '../../api/contacts.ts';
import { field, hint, label } from './Sheet.tsx';

/**
 * Why a contact, a link or a company changed: one of the four reasons shared by every edit in Procura
 * (Arthur, 16 October), with text required only for Other. The words are the same everywhere: visits,
 * log entries and documents ask the same question.
 */
export function ReasonFields({ id, reason, text, onReason, onText }: {
  id: string;
  reason: EditReason | null;
  text: string;
  onReason: (r: EditReason) => void;
  onText: (t: string) => void;
}) {
  const reasons = useEditReasons();
  const needsText = reasons.find((r) => r.code === reason)?.needs_text === true;
  return (
    <fieldset className="mt-3">
      <legend className={label}>Why</legend>
      <div className="mt-1 flex flex-wrap gap-2">
        {reasons.length === 0 && <p className={hint}>Loading the reasons…</p>}
        {reasons.map((r) => (
          <label key={r.code}
                 className={`inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm
                   ${reason === r.code ? 'border-brand bg-tint font-semibold text-neutral-900' : 'border-neutral-300 text-neutral-800'}`}>
            <input type="radio" name={`${id}-reason`} value={r.code} checked={reason === r.code}
                   onChange={() => onReason(r.code)} className="h-4 w-4" />
            {r.label}
          </label>
        ))}
      </div>
      <label htmlFor={`${id}-reason-text`} className={`${label} mt-2`}>
        {needsText ? 'Say what the reason is' : 'Anything to add (optional)'}
      </label>
      <input id={`${id}-reason-text`} className={field} value={text} onChange={(e) => onText(e.target.value)}
             maxLength={2000} aria-required={needsText} />
      {needsText && !text.trim() && <p className={hint}>This reason needs a few words.</p>}
    </fieldset>
  );
}

export function reasonReady(reasons: EditReasonRow[], reason: EditReason | null, text: string): boolean {
  const r = reasons.find((x) => x.code === reason);
  if (!r) return false;
  return !(r.needs_text && !text.trim());
}
